import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatCard } from "@/components/app/StatCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { LogIn, LogOut, Coffee, Play, Clock, Loader2, CalendarOff, Calendar, FileText, Download, ClipboardCheck, X } from "lucide-react";
import { toast } from "sonner";
import LeaveCalendar from "@/components/app/LeaveCalendar";
import LeaveRequests from "@/components/app/LeaveRequests";
import ImpactCheckoutModal from "@/components/app/ImpactCheckoutModal";
import GroomingCaptureModal from "@/components/app/GroomingCaptureModal";
import { Calendar as DayCalendar } from "@/components/ui/calendar";
import { API, getToken } from "@/lib/api";
import { logger } from "@/lib/logger";

// Curated rotating messages — no LLM cost. Pick one at random per click.
const BREAK_START_MESSAGES = [
  "Enjoy your break — you've earned it.",
  "Take a breath. Stretch. Sip something warm. We'll be here when you're back.",
  "Step away with a clear conscience — the world won't end in 15 minutes.",
  "Recharge time. Be kind to yourself.",
  "Good breaks make for great afternoons. See you soon!",
];
const BREAK_END_MESSAGES = [
  "Welcome back — let's pick up where we left off.",
  "Good to see you. Let's make the rest of the day count.",
  "Refreshed and ready? Onwards!",
  "Welcome back — the team's been holding the fort.",
  "Back in action. Go gentle, finish strong.",
];
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function fmtHMS(seconds) {
  if (!seconds || seconds < 0) return "0h 0m";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
}

function StateBadge({ state }) {
  const map = {
    out: { bg: "bg-zinc-100", text: "text-zinc-600", label: "Clocked Out" },
    working: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Working" },
    on_break: { bg: "bg-amber-50", text: "text-amber-700", label: "On Break" },
  };
  const m = map[state] || map.out;
  return (
    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${m.bg} ${m.text}`}>{m.label}</span>
  );
}

function LeaveBadge({ leave }) {
  if (!leave) return null;
  const map = {
    off: { bg: "bg-blue-50", text: "text-blue-700", label: "Day Off" },
    absent: { bg: "bg-red-50", text: "text-red-700", label: "Absent" },
    half_day: { bg: "bg-amber-50", text: "text-amber-700", label: "Half Day" },
  };
  const catMap = { el: "EL", sl: "SL", cl: "CL", weekly_off: "WO", lop: "LOP" };
  const m = map[leave.type] || map.off;
  const cat = catMap[leave.leave_category];
  return (
    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${m.bg} ${m.text}`} title={leave.note || ""}>
      {m.label}{cat ? ` · ${cat}` : ""}
    </span>
  );
}


export default function Attendance() {
  const { user } = useAuth();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState(null);
  const [staff, setStaff] = useState([]);
  const [days, setDays] = useState(7);
  const [staffDays, setStaffDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("me");
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ user_id: "", user_name: "", dates: [], type: "off", leave_category: "weekly_off", note: "" });
  const sortedLeaveDates = useMemo(() => [...leaveForm.dates].sort(), [leaveForm.dates]);
  // Live tick for displaying running time
  const [, setTick] = useState(0);
  useEffect(() => { const i = setInterval(() => setTick((x) => x + 1), 30000); return () => clearInterval(i); }, []);

  const isAdmin = ["super_admin", "general_manager", "property_admin", "supervisor"].includes(user?.role);
  const { hasPermission } = useAuth();
  const canViewTeam = hasPermission("attendance_team");
  const canViewCalendar = hasPermission("attendance_calendar");
  const canViewRequests = hasPermission("leave_requests");

  const loadCurrent = useCallback(async () => {
    const { data } = await api.get("/attendance/current");
    setCurrent(data);
  }, []);
  const loadHistory = useCallback(async (d = days) => {
    const { data } = await api.get(`/attendance/me?days=${d}`);
    setHistory(data);
  }, [days]);
  const loadStaff = useCallback(async (d = staffDays) => {
    const { data } = await api.get(`/attendance/staff?days=${d}`);
    setStaff(data);
  }, [staffDays]);

  useEffect(() => { loadCurrent(); loadHistory(days); }, [loadCurrent, loadHistory, days]);
  useEffect(() => { if (tab === "team" && isAdmin) loadStaff(staffDays); }, [tab, staffDays, isAdmin, loadStaff]);

  const [impactOpen, setImpactOpen] = useState(false);
  const [groomingFor, setGroomingFor] = useState(null); // "clock_in" | "break_end"

  const postEvent = async (type, photos) => {
    setBusy(true);
    try {
      await api.post("/attendance/event", { type, ...(photos || {}) });
      const labels = { clock_in: "Clocked in", clock_out: "Clocked out", break_start: "Break started", break_end: "Break ended" };
      // Curated positive nudge for break transitions; default toast otherwise.
      if (type === "break_start") toast.success(pick(BREAK_START_MESSAGES), { duration: 5000 });
      else if (type === "break_end") toast.success(pick(BREAK_END_MESSAGES), { duration: 5000 });
      else toast.success(labels[type]);
      if (type === "clock_in" || type === "break_end") {
        toast.message("Grooming photos submitted — we'll review them shortly.");
      }
      setGroomingFor(null);
      await loadCurrent();
      await loadHistory(days);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const trigger = async (type) => {
    // Shift-start punches require the 3 grooming photos.
    if (type === "clock_in" || type === "break_end") {
      setGroomingFor(type);
      return;
    }
    if (type !== "clock_out") return postEvent(type);
    // Clock-out is gated behind the impact reflection.
    try {
      const { data } = await api.get("/attendance/impact/today");
      if (data.has_submitted) return postEvent("clock_out");
    } catch (e) {
      // If the impact check fails, fall through to the modal — better UX than blocking clock-out.
      logger.warn("impact/today check failed, opening modal anyway", e);
    }
    setImpactOpen(true);
  };

  const openLeaveDialog = (s) => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const existingType = s.leave_today?.type || "off";
    setLeaveForm({
      user_id: s.user_id,
      user_name: s.name,
      dates: s.leave_today?.date ? [s.leave_today.date] : [todayStr],
      type: existingType,
      leave_category: s.leave_today?.leave_category || (existingType === "off" ? "weekly_off" : "lop"),
      note: s.leave_today?.note || "",
    });
    setLeaveOpen(true);
  };

  const submitLeave = async () => {
    if (!leaveForm.dates || leaveForm.dates.length === 0) {
      toast.error("Pick at least one date");
      return;
    }
    try {
      const { data } = await api.post("/leaves/bulk", {
        user_id: leaveForm.user_id,
        dates: leaveForm.dates,
        type: leaveForm.type,
        leave_category: leaveForm.leave_category,
        note: leaveForm.note || null,
      });
      const label = leaveForm.type.replace("_", " ");
      toast.success(
        data.total === 1
          ? `Marked ${label} for ${leaveForm.user_name}`
          : `Marked ${label} on ${data.total} dates for ${leaveForm.user_name}`
      );
      setLeaveOpen(false);
      loadStaff(staffDays);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const clearLeaveToday = async (s) => {
    if (!s.leave_today) return;
    if (!window.confirm(`Remove ${s.leave_today.type.replace("_", " ")} mark for ${s.name}?`)) return;
    await api.delete(`/leaves/${s.leave_today.id}`);
    toast.success("Leave entry removed");
    loadStaff(staffDays);
  };

  const exportCSV = async () => {
    try {
      const token = getToken();
      const resp = await fetch(`${API}/attendance/export?days=${staffDays}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error("Export failed");
      logger.error("attendance export failed", e);
    }
  };


  // Compute live total including ongoing session
  let liveWorked = current?.worked_seconds_today || 0;
  let liveBreak = current?.break_seconds_today || 0;
  if (current?.since) {
    const elapsed = Math.floor((Date.now() - new Date(current.since).getTime()) / 1000);
    if (current.state === "working") liveWorked = (current.worked_seconds_today || 0) + Math.max(0, elapsed);
    if (current.state === "on_break") liveBreak = (current.break_seconds_today || 0) + Math.max(0, elapsed);
  }

  return (
    <div className="space-y-6">
      <div>
        <span className="label-overline">Time Tracking</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Attendance</h1>
        <p className="text-sm text-zinc-500 mt-2">Clock in/out, manage breaks, and review hours logged.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="attendance-tabs">
          <TabsTrigger value="me" data-testid="tab-me">My Attendance</TabsTrigger>
          {canViewTeam && <TabsTrigger value="team" data-testid="tab-team">Team Hours</TabsTrigger>}
          {canViewCalendar && <TabsTrigger value="calendar" data-testid="tab-calendar"><Calendar className="w-3.5 h-3.5 mr-1" />Calendar</TabsTrigger>}
          {canViewRequests && <TabsTrigger value="requests" data-testid="tab-requests"><ClipboardCheck className="w-3.5 h-3.5 mr-1" />Requests</TabsTrigger>}
        </TabsList>

        <TabsContent value="me" className="space-y-6 mt-4">
          {/* Clock controls */}
          <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="clock-card">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <span className="label-overline">Status</span>
                <div className="mt-1 flex items-center gap-2">
                  <StateBadge state={current?.state || "out"} />
                  {current?.since && (
                    <span className="text-xs text-zinc-500">since {fmtTime(current.since)}</span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <span className="label-overline">Today</span>
                <div className="font-display text-3xl font-black tracking-tight mt-1">{fmtHMS(liveWorked)}</div>
                <div className="text-[11px] text-zinc-500">break {fmtHMS(liveBreak)}</div>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Button
                onClick={() => trigger("clock_in")}
                disabled={busy || current?.state !== "out"}
                className="h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                data-testid="btn-clock-in"
              >
                <LogIn className="w-4 h-4 mr-2" /> Clock In
              </Button>
              <Button
                onClick={() => trigger("break_start")}
                disabled={busy || current?.state !== "working"}
                variant="outline"
                className="h-14 border-amber-300 text-amber-700 hover:bg-amber-50 font-bold"
                data-testid="btn-break-start"
              >
                <Coffee className="w-4 h-4 mr-2" /> Start Break
              </Button>
              <Button
                onClick={() => trigger("break_end")}
                disabled={busy || current?.state !== "on_break"}
                variant="outline"
                className="h-14 border-blue-300 text-blue-700 hover:bg-blue-50 font-bold"
                data-testid="btn-break-end"
              >
                <Play className="w-4 h-4 mr-2" /> End Break
              </Button>
              <Button
                onClick={() => trigger("clock_out")}
                disabled={busy || current?.state === "out"}
                className="h-14 bg-zinc-950 hover:bg-zinc-800 text-white font-bold"
                data-testid="btn-clock-out"
              >
                <LogOut className="w-4 h-4 mr-2" /> Clock Out
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatCard label={`Last ${days} days · Worked`} value={fmtHMS(history?.total_worked_seconds)} accent="green" testid="stat-period-worked" />
            <StatCard label={`Last ${days} days · Break`} value={fmtHMS(history?.total_break_seconds)} accent="amber" testid="stat-period-break" />
            <StatCard label="Days Present" value={history?.daily?.length ?? 0} testid="stat-days-present" />
          </div>

          {/* Period selector + Daily list */}
          <div className="bg-white border border-zinc-200 rounded-md p-6">
            <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
              <div>
                <span className="label-overline">History</span>
                <h3 className="font-display text-xl font-bold tracking-tight mt-1">Daily breakdown</h3>
              </div>
              <Select value={String(days)} onValueChange={(v) => { setDays(Number(v)); loadHistory(Number(v)); }}>
                <SelectTrigger className="w-[160px]" data-testid="period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(!history?.daily || history.daily.length === 0) ? (
              <div className="text-zinc-500 text-sm py-8 text-center">No attendance recorded in this period.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 text-zinc-500 uppercase text-[10px] tracking-widest">
                      <th className="text-left py-2">Date</th>
                      <th className="text-left py-2">First In</th>
                      <th className="text-left py-2">Last Out</th>
                      <th className="text-right py-2">Worked</th>
                      <th className="text-right py-2">Break</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.daily.slice().reverse().map((d) => {
                      const firstIn = d.events.find((e) => e.type === "clock_in")?.timestamp;
                      const lastOut = [...d.events].reverse().find((e) => e.type === "clock_out")?.timestamp;
                      return (
                        <tr key={d.date} className="border-b border-zinc-100" data-testid={`day-row-${d.date}`}>
                          <td className="py-2.5">
                            <div className="flex items-center gap-2">
                              <span>{fmtDate(d.date)}</span>
                              {d.leave && <LeaveBadge leave={d.leave} />}
                            </div>
                          </td>
                          <td className="py-2.5 font-mono">{fmtTime(firstIn)}</td>
                          <td className="py-2.5 font-mono">{fmtTime(lastOut)}</td>
                          <td className="py-2.5 text-right font-mono font-semibold">{fmtHMS(d.worked_seconds)}</td>
                          <td className="py-2.5 text-right font-mono text-zinc-500">{fmtHMS(d.break_seconds)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="team" className="space-y-4 mt-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="label-overline">Team summary</span>
              <div className="flex gap-2">
                <Select value={String(staffDays)} onValueChange={(v) => setStaffDays(Number(v))}>
                  <SelectTrigger className="w-[160px]" data-testid="team-period-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Today</SelectItem>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="14">Last 14 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={exportCSV} data-testid="export-csv">
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
                    <th className="py-3 px-4">Name</th>
                    <th className="py-3 px-4">Role</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Today</th>
                    <th className="py-3 px-4 text-right">Leaves</th>
                    <th className="py-3 px-4 text-right">Days Present</th>
                    <th className="py-3 px-4 text-right">Hours Worked</th>
                    <th className="py-3 px-4 text-right">Break</th>
                    <th className="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.length === 0 && (
                    <tr><td colSpan={9} className="py-10 text-center text-zinc-500">No attendance data yet.</td></tr>
                  )}
                  {staff.map((s) => (
                    <tr key={s.user_id} className="border-b border-zinc-100" data-testid={`team-row-${s.user_id}`}>
                      <td className="py-2.5 px-4 font-medium">{s.name}</td>
                      <td className="py-2.5 px-4 capitalize text-zinc-600">{s.role.replace("_", " ")}</td>
                      <td className="py-2.5 px-4"><StateBadge state={s.current_state} /></td>
                      <td className="py-2.5 px-4">
                        {s.leave_today ? (
                          <button onClick={() => clearLeaveToday(s)} className="cursor-pointer" data-testid={`clear-leave-${s.user_id}`}>
                            <LeaveBadge leave={s.leave_today} />
                          </button>
                        ) : (
                          <span className="text-zinc-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-2.5 px-4 text-right font-mono text-zinc-500">{s.leaves_in_period ?? 0}</td>
                      <td className="py-2.5 px-4 text-right font-mono">{s.days_present}</td>
                      <td className="py-2.5 px-4 text-right font-mono font-semibold">{fmtHMS(s.worked_seconds)}</td>
                      <td className="py-2.5 px-4 text-right font-mono text-zinc-500">{fmtHMS(s.break_seconds)}</td>
                      <td className="py-2.5 px-4 text-right">
                        <Button size="sm" variant="outline" onClick={() => openLeaveDialog(s)} data-testid={`mark-leave-${s.user_id}`}>
                          <CalendarOff className="w-3 h-3 mr-1" />
                          Mark leave
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="calendar" className="mt-4">
            <LeaveCalendar />
          </TabsContent>
        )}

        <TabsContent value="requests" className="mt-4">
          <LeaveRequests />
        </TabsContent>
      </Tabs>

      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Leave · {leaveForm.user_name}</DialogTitle>
            <DialogDescription>Pick one or more dates to mark as off, absent, or half-day.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between">
                <Label>Dates ({leaveForm.dates.length} selected)</Label>
                {leaveForm.dates.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setLeaveForm({ ...leaveForm, dates: [] })}
                    className="text-[11px] text-zinc-500 hover:text-zinc-900 uppercase tracking-widest"
                    data-testid="leave-dates-clear"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="border border-zinc-200 rounded-md mt-1 flex justify-center" data-testid="leave-multi-calendar">
                <DayCalendar
                  mode="multiple"
                  selected={leaveForm.dates.map((d) => new Date(`${d}T00:00:00`))}
                  onSelect={(arr) => {
                    const ds = (arr || []).map((d) => {
                      const y = d.getFullYear();
                      const m = String(d.getMonth() + 1).padStart(2, "0");
                      const day = String(d.getDate()).padStart(2, "0");
                      return `${y}-${m}-${day}`;
                    });
                    setLeaveForm({ ...leaveForm, dates: ds });
                  }}
                />
              </div>
              {leaveForm.dates.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2 max-h-20 overflow-auto" data-testid="leave-dates-chips">
                  {sortedLeaveDates.map((d) => (
                    <span key={d} className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-700 text-[11px] font-mono px-2 py-0.5 rounded">
                      {d}
                      <button
                        type="button"
                        onClick={() => setLeaveForm({ ...leaveForm, dates: leaveForm.dates.filter((x) => x !== d) })}
                        className="hover:text-red-600"
                        aria-label={`Remove ${d}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Type</Label>
              <Select
                value={leaveForm.type}
                onValueChange={(v) => {
                  // When user picks a type, auto-suggest a sensible category but keep their pick if any
                  const suggested = v === "off" ? "weekly_off" : "lop";
                  setLeaveForm({ ...leaveForm, type: v, leave_category: leaveForm.leave_category || suggested });
                }}
              >
                <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Day Off</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="half_day">Half Day</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Counts against</Label>
              <Select value={leaveForm.leave_category} onValueChange={(v) => setLeaveForm({ ...leaveForm, leave_category: v })}>
                <SelectTrigger data-testid="leave-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="el">Earned Leave (EL)</SelectItem>
                  <SelectItem value="sl">Sick Leave (SL)</SelectItem>
                  <SelectItem value="cl">Casual Leave (CL)</SelectItem>
                  <SelectItem value="weekly_off">Weekly Off (no balance impact)</SelectItem>
                  <SelectItem value="lop">Loss of Pay (LOP)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-500 mt-1">
                Determines which balance bucket this leave deducts from.
              </p>
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea rows={2} value={leaveForm.note} onChange={(e) => setLeaveForm({ ...leaveForm, note: e.target.value })} data-testid="leave-note" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitLeave} className="bg-zinc-950 hover:bg-zinc-800" data-testid="leave-submit">
              Save {leaveForm.dates.length > 0 ? `(${leaveForm.dates.length})` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImpactCheckoutModal
        open={impactOpen}
        onClose={() => setImpactOpen(false)}
        onComplete={() => postEvent("clock_out")}
      />
      <GroomingCaptureModal
        open={!!groomingFor}
        eventLabel={groomingFor === "break_end" ? "End break" : "Clock in"}
        busy={busy}
        onCancel={() => setGroomingFor(null)}
        onSubmit={(photos) => postEvent(groomingFor, photos)}
      />
    </div>
  );
}
